import { AutocompleteItem } from "@heroui/autocomplete";
import { Button, Card } from "@heroui/react";
import { Icon } from "@iconify/react";
import type { CalendarDate } from "@internationalized/date";
import { type AppFormFieldApi, useLanguage } from "@onesolution/hooks";
import { getItemPageByValuesFromServer } from "@onesolution/serverFn";
import type { AnyFieldApi } from "@tanstack/react-form";
import type { ItemSelect } from "../form";
import type { Product, RequestedProductFieldApi } from "./OperationForm";
import { pricesQueryParams } from "./queriesParams";

export function OperationProductForm({
	index,
	requestedProductsField,
	defaultValues,
}: {
	index: number;
	requestedProductsField: AppFormFieldApi;
	defaultValues: string;
}) {
	const { t, locale } = useLanguage();
	const concernedProduct = requestedProductsField.state.value[index];
	const prices = getItemPageByValuesFromServer.useQuery(pricesQueryParams);

	const onProductSelectionChange = (
		fieldToSet: AnyFieldApi,
		indexToSet: number,
		setTo: Partial<RequestedProductFieldApi> | null,
	) => {
		const newRequestedProducts = fieldToSet.state.value
			.map((fieldData: RequestedProductFieldApi, idx: number) => {
				if (indexToSet === idx) {
					return setTo ? { ...fieldData, ...setTo } : null;
				}

				return fieldData;
			})
			.filter((f: RequestedProductFieldApi) => f);
		fieldToSet.handleChange(newRequestedProducts);
	};

	return (
		<Card className="flex items-center gap-2 p-2 px-3 pb-4">
			<div className="flex justify-between w-full h-6 items-center">
				<h6>
					{t("name.product")}#{index + 1}
				</h6>
				<Button
					isIconOnly
					variant="light"
					onPress={() =>
						onProductSelectionChange(requestedProductsField, index, null)
					}
				>
					<Icon icon="solar:close-circle-linear" />
				</Button>
			</div>
			<div className="w-full flex justify-between gap-2">
				<requestedProductsField.Autocomplete
					aria-label="select product"
					className="w-fit grow"
					isLoading={prices.isPending}
					defaultItems={prices.data || []}
					size="lg"
					label={t("name.product")}
					isClearable={false}
					isVirtualized={false}
					onSelectionChange={(selection) =>
						onProductSelectionChange(
							requestedProductsField,
							index,
							selection
								? {
										product: prices.data?.find(
											({ id }) => id === selection,
										) as Product,
									}
								: null,
						)
					}
					defaultSelectedKey={defaultValues}
				>
					{(rawPrice) => {
						const price = rawPrice as ItemSelect;
						const selected = requestedProductsField.state.value.some(
							(rp: RequestedProductFieldApi) => rp?.product?.id === price.id,
						);

						return (
							<AutocompleteItem
								textValue={price.name}
								key={price.id}
								className={`${selected && "opacity-20 pointer-events-none"} `}
							>
								<p>{price.name}</p>
							</AutocompleteItem>
						);
					}}
				</requestedProductsField.Autocomplete>
				<requestedProductsField.NumberInput
					isRequired
					aria-label="quantity"
					className="w-22"
					label={t("basic.quantity")}
					labelPlacement="outside"
					classNames={{
						label: "-top-8",
						inputWrapper: "top-8 h-12 rounded-xl",
					}}
					value={concernedProduct.quantity}
					onValueChange={(quantity: number) =>
						onProductSelectionChange(requestedProductsField, index, {
							quantity: quantity >= 1 ? quantity : 1,
						})
					}
				/>
			</div>
			<requestedProductsField.DatePicker
				label={`${t("basic.deadline")} (${t("basic.optional")})`}
				value={concernedProduct.deliveryDate}
				onChange={(e) => {
					const deliveryDate = e as unknown as CalendarDate;

					onProductSelectionChange(requestedProductsField, index, {
						deliveryDate,
					});
				}}
				startContent={
					concernedProduct.deliveryDate ? (
						<Icon
							className="cursor-pointer min-w-4 text-foreground"
							icon="solar:close-circle-line-duotone"
							width={24}
							height={24}
							onClick={() =>
								onProductSelectionChange(requestedProductsField, index, {
									deliveryDate: null,
								})
							}
						/>
					) : null
				}
			/>

			{concernedProduct.product?.prices ? (
				<div className="rounded-xl bg-secondary-100 p-4 flex text-secondary-700 w-full">
					<Icon
						icon="solar:info-circle-bold"
						className="text-2xl text-secondary-500 mr-4"
					/>
					<div className="flex flex-col">
						<span className="font-bold">{t("operation.total-title")}</span>
						{Number.parseFloat(
							concernedProduct.product.prices.toString(),
						)?.toLocaleString(locale, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
						€ {t("operation.per-unit")}
					</div>
				</div>
			) : null}
		</Card>
	);
}
